import { useIntl } from 'react-intl';

import Table from '../../../../components/Table';

import type { Quota, VirtualCloud } from '@/types/cloudTypes';
import type { TableRowData } from '@/types/tableTypes';

export interface VirtualCloudsSummaryProps {
  virtualCloud?: VirtualCloud | undefined;
}

function ramToGigabytes(ram: number): number {
  return Math.floor(ram / 1024);
}

function convertMegabytesToGigabytes(megabytes: number) {
  return megabytes / 1024;
}

const fields = [
  {
    title: { id: 'quota' },
    id: 'quota',
  },
  {
    title: { id: 'vCPU' },
    id: 'vCPU',
  },
  {
    title: { id: 'ramGb' },
    id: 'RAM',
  },
  {
    title: { id: 'volumesGb' },
    id: 'volumes',
  },
  {
    title: { id: 'objectStorageGb' },
    id: 'objectStorage',
  },
  {
    title: { id: 'floatingIps' },
    id: 'floatingIPs',
  },
];

const getRows = (
  allowedQuota: Quota,
  usedQuota: Quota,
  currentAllowedLabel: string,
  currentAllocatedLabel: string
): TableRowData[] => [
  {
    key: 'allowed-quota',
    data: [
      {
        field: 'quota',
        value: currentAllowedLabel,
      },
      {
        field: 'vCPU',
        value: allowedQuota.compute.cores,
      },
      {
        field: 'RAM',
        value: ramToGigabytes(allowedQuota.compute.ram),
      },
      {
        field: 'volumes',
        value: allowedQuota.volume.gigabytes,
      },
      {
        field: 'objectStorage',
        value: convertMegabytesToGigabytes(allowedQuota.volume.objects_megabytes),
      },
      {
        field: 'floatingIPs',
        value: allowedQuota.network.floating_ips,
      },
    ],
  },
  {
    key: 'allocated-quota',
    data: [
      {
        field: 'quota',
        value: currentAllocatedLabel,
      },
      {
        field: 'vCPU',
        value: usedQuota.compute.cores,
      },
      {
        field: 'RAM',
        value: ramToGigabytes(usedQuota.compute.ram),
      },
      {
        field: 'volumes',
        value: usedQuota.volume.gigabytes,
      },
      {
        field: 'objectStorage',
        value: usedQuota.volume.objects_megabytes,
      },
      {
        field: 'floatingIPs',
        value: usedQuota.network.floating_ips,
      },
    ],
  },
];

const VirtualCloudsSummary: React.FC<VirtualCloudsSummaryProps> = ({ virtualCloud }) => {
  const intl = useIntl();
  if (!virtualCloud) return <p>{intl.formatMessage({ id: 'noVirtualCloudsFound' })}</p>;

  return (
    <Table
      fields={fields}
      rows={getRows(
        virtualCloud.allowed_quota,
        virtualCloud.used_quota,
        intl.formatMessage({ id: 'currentAllowed' }),
        intl.formatMessage({ id: 'currentAllocated' })
      )}
      search={false}
      pagination={false}
    />
  );
};

export default VirtualCloudsSummary;
